import { Texture } from "@/core/backends/webgl/texture"
import { RawDemosaicer, RawDemosaicerOptions } from "./raw-demosaicer"
import { vec4 } from "gl-matrix"
import { Renderer } from "@/core/pipeline/renderer"
import { DualBuffers } from "@/core/pipeline/dual-buffers"

export interface ChannelMultipliers {
	r: vec4
	g: vec4
	b: vec4
}

const BAYER_RED = 0
const BAYER_GREEN = 1
const BAYER_BLUE = 2
const BAYER_BLOCK_OFFSETS: Array<readonly [number, number]> = [
	[0, 0],
	[1, 0],
	[0, 1],
	[1, 1],
]

export function getBayerHalfSizeChannelMultipliers(
	cfa: ArrayLike<number>,
	offset: readonly [number, number] = [0, 0],
): ChannelMultipliers {
	const multipliers: ChannelMultipliers = {
		r: [0.0, 0.0, 0.0, 0.0],
		g: [0.0, 0.0, 0.0, 0.0],
		b: [0.0, 0.0, 0.0, 0.0],
	}

	let greenCount = 0

	for (let i = 0; i < BAYER_BLOCK_OFFSETS.length; i++) {
		const blockOffset = BAYER_BLOCK_OFFSETS[i] ?? [0, 0]
		const x = positiveModulo(offset[0] + blockOffset[0], 2)
		const y = positiveModulo(offset[1] + blockOffset[1], 2)
		const color = cfa[x + y * 2]

		switch (color) {
			case BAYER_RED:
				multipliers.r[i] = 1.0
				break
			case BAYER_GREEN:
				multipliers.g[i] = 1.0
				greenCount++
				break
			case BAYER_BLUE:
				multipliers.b[i] = 1.0
				break
		}
	}

	if (greenCount > 0) {
		for (let i = 0; i < multipliers.g.length; i++) {
			multipliers.g[i] = (multipliers.g[i] ?? 0) / greenCount
		}
	}

	return multipliers
}

function positiveModulo(value: number, divisor: number): number {
	return ((value % divisor) + divisor) % divisor
}

// Reference src:
// https://github.com/LuisSR/RCD-Demosaicing/blob/master/rcd_demosaicing.c
// https://github.com/RawTherapee/RawTherapee/blob/dev/rtengine/rcd_demosaic.cc

export class BayerRCDDemosaicer extends RawDemosaicer {
	demosaicHalfSize(
		inputTexture: Texture,
		buffers: DualBuffers,
		renderer: Renderer,
		options: RawDemosaicerOptions,
	) {
		const { whiteBalanceMultipliers, levels, info } = options

		const multipliers = getBayerHalfSizeChannelMultipliers(info.cfa_array)

		inputTexture.bind(0)
		buffers.writeBuffer.bindFramebuffer()

		renderer.backend.shaders.demosaicHalfInt.render({
			source: 0,
			offset: options.globalOffset,
			redMultiplier: multipliers.r,
			greenMultiplier: multipliers.g,
			blueMultiplier: multipliers.b,
			wbCoeffs: whiteBalanceMultipliers,
			blackLevel: levels[0],
			whitePoint: levels[1],
		})
	}

	demosaicFullSize(
		inputTexture: Texture,
		buffers: DualBuffers,
		renderer: Renderer,
		options: RawDemosaicerOptions,
	) {
		const { whiteBalanceMultipliers, levels, cfaArray } = options

		const dirTexture = buffers.readBuffer
		const greenTexture = buffers.writeBuffer
		const rgbTexture = dirTexture

		// pass 1: compute VH_Dir, PQ_Dir, lpf, scaledRaw
		inputTexture.bind(0)
		dirTexture.bindFramebuffer()

		renderer.backend.shaders.demosaicRCD1Int.render({
			source: 0,
			offset: options.globalOffset,
			wbCoeffs: whiteBalanceMultipliers,
			blackLevel: levels[0],
			whitePoint: levels[1],
			cfa: cfaArray,
		})

		// pass 2: interpolate green (using VH_Dir and lpf), get [VH_Dir, PQ_Dir, green, scaledRaw]
		dirTexture.bind(0)
		greenTexture.bindFramebuffer()
		renderer.backend.shaders.demosaicRCD2.render({
			dirTexture: 0,
			cfa: cfaArray,
		})

		// pass 3: interpolate red and blue (using PQ_Dir and green) at rb positions
		// get [r, g, b, VH_Dir]
		greenTexture.bind(0)
		rgbTexture.bindFramebuffer()
		renderer.backend.shaders.demosaicRCD3.render({
			greenTexture: 0,
			cfa: cfaArray,
		})

		// pass 4: interpolate red and blue (using VH_Dir and pass3 results) at green positions
		rgbTexture.bind(0)
		greenTexture.bindFramebuffer()

		renderer.backend.shaders.demosaicRCD4.render({
			rgbTexture: 0,
			cfa: cfaArray,
		})
	}
}
